import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { CalendarIcon, Send, Tag, User2, CalendarCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import { pt, enUS, fr, es } from "date-fns/locale";
import { format, differenceInCalendarDays } from "date-fns";
import { useT } from "../../i18n/LanguageContext";
import { trackEvent } from "../../analytics";
import { BLOCKED_RANGES } from "../../data/blockedDates";

// In production (Cloudflare Pages) REACT_APP_BACKEND_URL is empty/unset, so we
// fall back to a same-origin "/api" path that hits the Pages Function.
const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;
const LOCALES = { pt, en: enUS, fr, es };

// A agenda abre a partir desta data (1 de Julho de 2026)
const AGENDA_OPENS = new Date(2026, 6, 1);

// Estadia: mínimo 3 noites, máximo 31 noites
const MIN_NIGHTS = 3;
const MAX_NIGHTS = 31;

export default function Reserva() {
  const { t, lang } = useT();
  const locale = LOCALES[lang] || pt;

  const minDate = useMemo(() => {
    const today = new Date();
    return today > AGENDA_OPENS ? today : AGENDA_OPENS;
  }, []);

  const [otaRanges, setOtaRanges] = useState([]);
  useEffect(() => {
    fetch("/api/blocked-dates")
      .then((r) => (r.ok ? r.json() : { ranges: [] }))
      .then((data) => setOtaRanges(Array.isArray(data?.ranges) ? data.ranges : []))
      .catch(() => {});
  }, []);

  const disabledDays = useMemo(() => {
    const parse = (s) => {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d);
    };
    return [
      { before: minDate },
      ...BLOCKED_RANGES.map((r) => ({ from: parse(r.from), to: parse(r.to) })),
      ...otaRanges.map((r) => ({ from: parse(r.from), to: parse(r.to) })),
    ];
  }, [minDate, otaRanges]);

  const [range, setRange] = useState(undefined);
  const [adultos, setAdultos] = useState("");
  const [bebes, setBebes] = useState("0");
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [enviando, setEnviando] = useState(false);

  const datasLabel = useMemo(() => {
    if (range?.from && range?.to) return `${format(range.from, "dd MMM", { locale })} → ${format(range.to, "dd MMM yyyy", { locale })}`;
    if (range?.from) return `${format(range.from, "dd MMM yyyy", { locale })} → …`;
    return t.booking.form.datesPlaceholder;
  }, [range, locale, t.booking.form.datesPlaceholder]);

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) { toast.error(t.booking.toast.needDates); return; }
    const nights = differenceInCalendarDays(range.to, range.from);
    if (nights < MIN_NIGHTS) { toast.error(t.booking.toast.minNights); return; }
    if (nights > MAX_NIGHTS) { toast.error(t.booking.toast.maxNights); return; }
    if (!adultos) { toast.error(t.booking.toast.needAdults); return; }
    if (!nome.trim() || !email.trim()) { toast.error(t.booking.toast.needNameEmail); return; }
    setEnviando(true);
    try {
      await axios.post(`${API}/booking-requests`, {
        nome: nome.trim(),
        email: email.trim(),
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        adultos: Number(adultos),
        bebes: Number(bebes || 0),
        mensagem: mensagem.trim(),
      });
      toast.success(t.booking.toast.success);
      trackEvent("booking_request_submitted", {
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        adultos: Number(adultos),
        bebes: Number(bebes || 0),
        currency: "EUR",
        value: 1,
      });
      setRange(undefined); setAdultos(""); setBebes("0"); setNome(""); setEmail(""); setMensagem("");
    } catch (err) {
      const detail = err?.response?.data?.detail || t.booking.toast.error;
      toast.error(typeof detail === "string" ? detail : t.booking.toast.error);
    } finally {
      setEnviando(false);
    }
  };

  const highlights = t.booking.highlights;
  const icons = [Tag, User2, CalendarCheck, Zap];

  return (
    <section id="reserva" className="py-20 md:py-28 bg-noite text-[#faf6ee] relative overflow-hidden" data-testid="reserva-section">
      <div className="absolute -top-40 right-0 w-[30rem] h-[30rem] rounded-full bg-[#c89033]/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-32 w-[30rem] h-[30rem] rounded-full bg-[#2c5fb5]/25 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start relative">
        <div className="reveal">
          <p className="section-label">{t.booking.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
            {t.booking.title1} <em className="text-[#e3b25b]">{t.booking.title2}</em>
          </h2>
          <p className="mt-4 text-[#faf6ee]/80 max-w-md leading-relaxed">{t.booking.subtitle}</p>

          <div className="mt-8 space-y-3">
            {highlights.map((h, i) => {
              const Icon = icons[i];
              return (
                <div key={h.t} className="flex gap-3 items-start bg-[#1a2c5c]/55 ring-1 ring-white/10 p-4 rounded-2xl">
                  <div className="w-10 h-10 rounded-xl bg-[#e3b25b] text-[#0e1a3f] flex items-center justify-center flex-shrink-0">
                    <Icon size={18} strokeWidth={2.2} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{h.t}</p>
                    <p className="text-xs text-[#faf6ee]/70">{h.d}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <form onSubmit={submit} className="reveal bg-[#faf6ee] text-[#0e1a3f] rounded-3xl p-6 sm:p-8 shadow-2xl ring-1 ring-[#0e1a3f]/15" data-testid="reserva-form">
          <h3 className="font-display text-2xl text-[#0e1a3f]">{t.booking.form.title}</h3>
          <p className="text-sm text-[#0e1a3f]/65">{t.booking.form.subtitle}</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#0e1a3f]/70 flex items-center gap-2">
                <CalendarIcon size={14} /> {t.booking.form.dates}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="reserva-datas-trigger" className="mt-1.5 w-full text-left bg-white border border-[#0e1a3f]/20 rounded-xl px-4 py-3 hover:border-[#0e1a3f]/50 transition">
                    {datasLabel}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={typeof window !== "undefined" && window.innerWidth >= 640 ? 2 : 1} locale={locale} disabled={disabledDays} defaultMonth={minDate} min={MIN_NIGHTS + 1} max={MAX_NIGHTS + 1} initialFocus />
                </PopoverContent>
              </Popover>
              <p className="text-[11px] text-[#0e1a3f]/55 mt-1">{t.booking.form.nightsHint}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#0e1a3f]/70">👤 {t.booking.form.adults}</label>
                <Select value={adultos} onValueChange={setAdultos}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="reserva-adultos">
                    <SelectValue placeholder={t.booking.form.select} />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} {n > 1 ? t.booking.form.adults_p : t.booking.form.adult} {n === 6 ? t.booking.form.max : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#0e1a3f]/55 mt-1">{t.booking.form.maxAdults}</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#0e1a3f]/70">👶 {t.booking.form.babies}</label>
                <Select value={bebes} onValueChange={setBebes}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="reserva-bebes">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} {n === 1 ? t.booking.form.baby : t.booking.form.babies_p} {n === 2 ? t.booking.form.max : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#0e1a3f]/55 mt-1">{t.booking.form.maxBabies}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#0e1a3f]/70">{t.booking.form.name}</label>
                <Input className="mt-1.5 bg-white" placeholder={t.booking.form.namePlaceholder} value={nome} onChange={(e) => setNome(e.target.value)} data-testid="reserva-nome" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#0e1a3f]/70">{t.booking.form.email}</label>
                <Input type="email" className="mt-1.5 bg-white" placeholder={t.booking.form.emailPlaceholder} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reserva-email" />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#0e1a3f]/70">{t.booking.form.message}</label>
              <Textarea className="mt-1.5 bg-white resize-none" rows={3} placeholder={t.booking.form.messagePlaceholder} value={mensagem} onChange={(e) => setMensagem(e.target.value)} data-testid="reserva-mensagem" />
            </div>

            <button type="submit" disabled={enviando} className="btn-primario w-full disabled:opacity-60 disabled:cursor-not-allowed" data-testid="reserva-submit">
              {enviando ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  {t.booking.form.sending}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send size={14} /> {t.booking.form.submit}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
